import { FC, useMemo, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { FormSearch, PrimaryButton } from '..'
import { useAppSelector } from '../../store/hooks'
import { ServiceDTO } from '../../interfaces/Services/Services'
import { ShipmentGroup } from '../../interfaces/Import/ShipmentGroupInterface'
import { CountryPhoneOption } from '../../interfaces/General/CountryPhoneDto'
import { filterCountryOption } from '../../services/GeneralServices'

interface OriginOption {
  buCode: string
  buName?: string
  location?: { name?: string }
}

/**
 * Barra de acciones masivas del Review de carga masiva.
 *
 * Aparece al pie cuando hay al menos una guía seleccionada y aplica tienda origen
 * y/o servicio a todas de una vez. Sin esto, un archivo de aliado de 403 guías con
 * contrato multi-oficina exige abrir, elegir y cerrar el acordeón 403 veces —
 * `validateGroup` pide `originStoreCode` por guía y no hay otra forma de ponerlo.
 *
 * Se muestran SOLO los campos que el contrato deja elegir: con una sola tienda
 * origen el front la auto-asigna y con un solo servicio el Review también, así que
 * en esos casos el campo no aparece (mismo criterio que la fila y que las columnas
 * condicionales de la plantilla Excel).
 *
 * Los campos son FormSearch (combo con buscador), como el resto del Review, y con
 * el desplegable hacia arriba porque la barra vive pegada al borde inferior.
 */
export const ShipmentBulkUploadActionBar: FC<{
  count: number
  serviceOptions?: ServiceDTO[]
  /** Países para "País del teléfono". Sin catálogo el campo no se ofrece. */
  countryOptions?: CountryPhoneOption[]
  onApply: (patch: Partial<ShipmentGroup>) => void
  onClear: () => void
}> = ({
  count,
  serviceOptions = [],
  countryOptions = [],
  onApply,
  onClear,
}) => {
  // Igual que la fila: se lee del store en vez de prop-drillear por dos niveles.
  const user = useAppSelector((s) => s.user)
  const originBusinessUnits = (user.originBusinessUnits ?? []) as OriginOption[]

  const [origin, setOrigin] = useState<OriginOption | undefined>()
  const [service, setService] = useState<ServiceDTO | undefined>()
  const [country, setCountry] = useState<CountryPhoneOption | undefined>()
  // FormSearch es un combobox con buscador: el texto tecleado es estado propio y la
  // opción elegida es otro. Se mantienen los dos, igual que en la fila del acordeón.
  const [originSearch, setOriginSearch] = useState('')
  const [serviceSearch, setServiceSearch] = useState('')
  const [countrySearch, setCountrySearch] = useState('')

  const originLabel = (b?: OriginOption) =>
    b ? `${b.buCode} - ${b.location?.name || b.buName || ''}` : ''
  const serviceLabel = (s?: ServiceDTO) => s?.serviceShortName ?? ''

  // La barra va sobre fondo oscuro (`text-white`), y los inputs de FormText/FormSearch
  // no fijan color propio: sin esto el texto sale blanco sobre el fondo blanco del
  // input y no se lee lo seleccionado.
  const fieldClassName = 'text-gray-900 bg-white'

  const showOrigin = originBusinessUnits.length > 1
  const showService = serviceOptions.length > 1
  // El país SIEMPRE es elegible (a diferencia del origen y el servicio, que el
  // front auto-asigna cuando hay uno solo): es el campo que hace que un archivo
  // entero de destinatarios de otro país se arregle de una vez. Solo se oculta
  // si el catálogo /Country no cargó y no hay nada que ofrecer.
  const showCountry = countryOptions.length > 1

  // Nada que elegir → nada que aplicar. La barra no se renderiza en vez de
  // ofrecer un botón que no haría nada.
  const hasFields = showOrigin || showService || showCountry
  const patch = useMemo<Partial<ShipmentGroup>>(() => {
    const p: Partial<ShipmentGroup> = {}
    if (showOrigin && origin) p.originStoreCode = origin.buCode
    if (showService && service) p.serviceId = service.serviceID
    // Solo el código: los DÍGITOS de cada fila los reescribe el contenedor con
    // `changeCountryCode`, porque dependen de lo que esa fila tenga cargado.
    if (showCountry && country) p.consigneePhoneCountryCode = country.dialCode
    return p
  }, [showOrigin, origin, showService, service, showCountry, country])

  const canApply = Object.keys(patch).length > 0

  if (count === 0 || !hasFields) return null

  return (
    <div
      className="sticky bottom-0 z-20 mt-3 rounded-lg bg-gray-900 text-white px-4 py-3 shadow-lg flex flex-wrap items-end gap-3"
      role="region"
      aria-label="Acciones para las guías seleccionadas"
    >
      <span className="text-sm font-medium whitespace-nowrap self-center tabular-nums">
        {count} {count === 1 ? 'guía seleccionada' : 'guías seleccionadas'}
      </span>

      {showOrigin && (
        <FormSearch<OriginOption>
          id="bulk-action-origin"
          name="bulk-action-origin"
          label=""
          placeholder="Tienda origen"
          value={originSearch}
          options={originBusinessUnits}
          onChange={(e) => setOriginSearch(e.target.value)}
          onSelectOption={(opt) => {
            setOrigin(opt)
            setOriginSearch(originLabel(opt))
          }}
          onFilter={(opt, filter) => {
            // Contra el LABEL completo, igual que "Código de tienda" en la fila: al
            // reentrar el value ya es "MIA - Miami" y un filtro campo a campo lo
            // dejaría sin opciones.
            const q = (filter || '').toLowerCase().trim()
            return q === '' || originLabel(opt).toLowerCase().includes(q)
          }}
          RenderOption={({ option }) => (
            <div className="px-2 py-1 text-sm">{originLabel(option)}</div>
          )}
          className={`!h-9 ${fieldClassName}`}
          dropUp
          containerClassname="w-full sm:w-60"
        />
      )}

      {showService && (
        <FormSearch<ServiceDTO>
          id="bulk-action-service"
          name="bulk-action-service"
          label=""
          placeholder="Servicio"
          value={serviceSearch}
          options={serviceOptions}
          onChange={(e) => setServiceSearch(e.target.value)}
          onSelectOption={(opt) => {
            setService(opt)
            setServiceSearch(serviceLabel(opt))
          }}
          onFilter={(opt, filter) => {
            const q = (filter || '').toLowerCase().trim()
            return q === '' || serviceLabel(opt).toLowerCase().includes(q)
          }}
          RenderOption={({ option }) => (
            <div className="px-2 py-1 text-sm">{serviceLabel(option)}</div>
          )}
          className={`!h-9 ${fieldClassName}`}
          dropUp
          containerClassname="w-full sm:w-52"
        />
      )}

      {showCountry && (
        <FormSearch<CountryPhoneOption>
          id="bulk-action-phone-country"
          name="bulk-action-phone-country"
          label=""
          placeholder="País del teléfono"
          value={countrySearch}
          options={countryOptions}
          onChange={(e) => setCountrySearch(e.target.value)}
          onSelectOption={(opt) => {
            setCountry(opt)
            setCountrySearch(opt?.dialCode || '')
          }}
          // Igual que en la fila: escribir sin elegir de la lista no selecciona
          // nada, así que el texto vuelve al país elegido (o a vacío) en vez de
          // quedar afirmando algo que "Aplicar a N" no va a hacer.
          onChangeFocus={(focus: boolean) => {
            if (!focus) setCountrySearch(country?.dialCode || '')
          }}
          onFilter={filterCountryOption}
          RenderOption={({ option }) => (
            <div className="px-2 py-1 text-sm">{option.label}</div>
          )}
          className={`!h-9 ${fieldClassName}`}
          dropUp
          containerClassname="w-full sm:w-52"
        />
      )}

      <PrimaryButton
        className="h-9 text-sm px-4 ml-auto"
        disabled={!canApply}
        onClick={() => {
          onApply(patch)
          // Se limpian los campos, no la selección: es normal aplicar tienda origen
          // y después servicio sobre el mismo lote.
          setOrigin(undefined)
          setService(undefined)
          setCountry(undefined)
          setOriginSearch('')
          setServiceSearch('')
          setCountrySearch('')
        }}
      >
        Aplicar a {count}
      </PrimaryButton>

      <button
        type="button"
        onClick={onClear}
        className="h-9 px-2 text-gray-300 hover:text-white self-center"
        aria-label="Quitar la selección"
        title="Quitar la selección"
      >
        <XMarkIcon className="w-5 h-5" aria-hidden="true" />
      </button>
    </div>
  )
}
